/**
 * Запись голосового ответа в WAV.
 *
 * Ручка `upload-ai-audio` принимает только WAV, а `MediaRecorder` в WebView
 * пишет в webm (Android) или mp4 (iOS) — форматы, которые сервер отклоняет.
 * Поэтому звук снимается через Web Audio и собирается в WAV вручную:
 * это единственный способ получить нужный формат на обеих платформах без
 * тяжёлой библиотеки-перекодировщика.
 *
 * Частота приводится к 16 кГц моно: речи этого достаточно, а файл получается
 * в несколько раз меньше — на мобильной сети это заметно.
 */

const TARGET_SAMPLE_RATE = 16000;
/** Размер блока: меньше — чаще прерывания, больше — заметнее задержка уровня. */
const BUFFER_SIZE = 4096;

export interface VoiceRecording {
  blob: Blob;
  durationMs: number;
}

export interface VoiceRecorder {
  /** Остановить запись и получить готовый файл. */
  stop(): Promise<VoiceRecording>;
  /** Прервать без результата: микрофон освобождается. */
  cancel(): void;
  /** Текущий уровень сигнала от 0 до 1 — для визуализации. */
  readonly level: number;
}

/**
 * Отказ микрофона с понятной причиной.
 *
 * Общее «микрофон недоступен» не даёт человеку ничего сделать: запрет в
 * настройках, отсутствие устройства и клиент без доступа к записи требуют
 * разных действий.
 */
export class VoiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VoiceError';
  }
}

function describe(error: unknown): VoiceError {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return new VoiceError('Доступ к микрофону запрещён. Разрешите запись в настройках клиента');
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return new VoiceError('Микрофон не найден');
  }
  if (name === 'NotReadableError') {
    return new VoiceError('Микрофон занят другим приложением');
  }
  return new VoiceError(error instanceof Error && error.message ? error.message : 'Запись не началась');
}

export async function startVoiceRecording(): Promise<VoiceRecorder> {
  // В части WebView записи нет вовсе: без этой проверки обращение к
  // `mediaDevices` падало бы с невнятной ошибкой обращения к undefined
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new VoiceError('Этот клиент не даёт приложению доступ к микрофону');
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
  } catch (error) {
    throw describe(error);
  }

  const AudioContextClass =
    window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) {
    stream.getTracks().forEach((track) => track.stop());
    throw new VoiceError('Запись голоса не поддерживается этим клиентом');
  }

  const context = new AudioContextClass();
  // iOS запускает контекст в приостановленном состоянии до явного возобновления
  if (context.state === 'suspended') await context.resume();

  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(BUFFER_SIZE, 1, 1);

  const chunks: Float32Array[] = [];
  let level = 0;
  let stopped = false;

  processor.onaudioprocess = (event) => {
    if (stopped) return;
    const input = event.inputBuffer.getChannelData(0);
    chunks.push(new Float32Array(input));

    // Средняя громкость блока: по ней рисуется индикатор записи
    let sum = 0;
    for (const sample of input) sum += sample * sample;
    level = Math.min(1, Math.sqrt(sum / input.length) * 4);
  };

  // Короткий сигнал до подключения обработчика: человек слышит, что микрофон
  // включился, и звук не попадает в саму запись
  await beep(context);

  source.connect(processor);
  // Без подключения к выходу обработчик не вызывается в части браузеров
  processor.connect(context.destination);

  const startedAt = Date.now();

  const release = () => {
    stopped = true;
    processor.disconnect();
    source.disconnect();
    stream.getTracks().forEach((track) => track.stop());
    void context.close();
  };

  return {
    get level() {
      return level;
    },

    cancel() {
      release();
    },

    async stop() {
      const durationMs = Date.now() - startedAt;
      const sampleRate = context.sampleRate;
      release();

      const samples = downsample(merge(chunks), sampleRate, TARGET_SAMPLE_RATE);
      return { blob: encodeWav(samples, TARGET_SAMPLE_RATE), durationMs };
    },
  };
}

/** Сигнал начала записи: короткий мягкий тон вместо звукового файла. */
async function beep(context: AudioContext): Promise<void> {
  const duration = 0.12;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = 880;
  // Плавные фронты: резкий старт и обрыв слышны как щелчок
  gain.gain.setValueAtTime(0, context.currentTime);
  gain.gain.linearRampToValueAtTime(0.12, context.currentTime + 0.02);
  gain.gain.linearRampToValueAtTime(0, context.currentTime + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + duration);

  await new Promise((resolve) => setTimeout(resolve, duration * 1000 + 40));
  gain.disconnect();
}

function merge(chunks: Float32Array[]): Float32Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/** Понижение частоты усреднением: без него запись звучит выше и быстрее. */
function downsample(samples: Float32Array, from: number, to: number): Float32Array {
  if (to >= from) return samples;

  const ratio = from / to;
  const length = Math.floor(samples.length / ratio);
  const result = new Float32Array(length);

  for (let index = 0; index < length; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.min(Math.floor((index + 1) * ratio), samples.length);

    let sum = 0;
    for (let position = start; position < end; position += 1) sum += samples[position];
    result[index] = end > start ? sum / (end - start) : 0;
  }

  return result;
}

/** Сборка WAV: заголовок RIFF и 16-битный моно-звук. */
function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeText = (offset: number, text: string) => {
    for (let index = 0; index < text.length; index += 1) {
      view.setUint8(offset + index, text.charCodeAt(index));
    }
  };

  writeText(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeText(8, 'WAVE');
  writeText(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM без сжатия
  view.setUint16(22, 1, true); // моно
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // байт в секунду
  view.setUint16(32, 2, true); // выравнивание блока
  view.setUint16(34, 16, true); // бит на отсчёт
  writeText(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (const sample of samples) {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}
